import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocalState, useOnline } from '@ndrc/app-sdk/client';
import { useDesk } from '../desk/context';
import { ApiError, callCore, mutateCore } from '../lib/rpc';
import { t } from '../messages';
import { CommandState } from './MissionValidations';

export function MissionPreviewProblem({
  classId,
  runId,
  problemId,
  version,
  enabled,
}: {
  classId: string;
  runId: string;
  problemId: string;
  version: string;
  enabled: boolean;
}) {
  const { me } = useDesk();
  const online = useOnline();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [request, setRequest, stored] = useLocalState(
    `preview-problem:${me.id}:${runId}:${problemId}`,
    {
      key: crypto.randomUUID(),
      version,
      commandId: null as string | null,
      definitiveError: false,
    },
  );
  const receipt = useQuery({
    queryKey: ['core', 'commands.status', classId, request.commandId],
    queryFn: () =>
      callCore<{ status: string }>('commands.status', { classId, commandId: request.commandId }),
    enabled: !!request.commandId,
    retry: false,
    refetchInterval: (query) =>
      ['ok', 'failed', 'partial'].includes(query.state.data?.status ?? '') ? false : 2000,
  });
  return (
    <>
      {!stored && <p role="alert">{t('teacher.missions.storageError')}</p>}
      {error && <p role="alert">{error}</p>}
      {request.commandId && (
        <CommandState mode="operation" classId={classId} commandId={request.commandId} />
      )}
      <button
        type="button"
        className="ndrc-button"
        disabled={!enabled || !online || busy || !!request.commandId || request.definitiveError}
        onClick={() => {
          setBusy(true);
          setError('');
          void mutateCore<{ commandId: string }>(
            'missions.previewResolveProblem',
            {
              classId,
              runId,
              problemId,
              expectedVersion: request.version,
            },
            request.key,
          )
            .then((result) => setRequest({ ...request, commandId: result.commandId }))
            .catch((e: unknown) => {
              setError(e instanceof Error ? e.message : t('mission.student.error'));
              if (e instanceof ApiError && e.status >= 400 && e.status < 500)
                setRequest({ ...request, definitiveError: true });
            })
            .finally(() => setBusy(false));
        }}
      >
        {t('mission.problems.resolve')}
      </button>
      {(request.definitiveError || ['failed', 'partial'].includes(receipt.data?.status ?? '')) && (
        <button
          type="button"
          className="ndrc-button"
          disabled={!online || busy}
          onClick={() => {
            setRequest({
              key: crypto.randomUUID(),
              version,
              commandId: null,
              definitiveError: false,
            });
            setError('');
          }}
        >
          {t('mission.response.resume')}
        </button>
      )}
    </>
  );
}
